import { sentenceCase } from "change-case";
import { ZodSchema } from "zod";

import { FIELD_TO_FRIENDLY_NAME } from "@/src/lib/constants";
import {
  EnabledFields,
  RequiredFieldsForClassification,
  RequestedFieldsForClassification,
} from "@/src/lib/schema-types";
import { capitalizeFirst } from "@/src/lib/utils/capitalize";
import { formatZodIssueMessage } from "@/src/lib/utils/zod-issue-message";

export const updateObject = (obj: any, pathString: string, value: any) => {
  const path = pathString.split(".");

  const updateNestedProperty = (obj: any, path: string[], value: any) => {
    const [head, ...rest] = path;

    if (!rest.length) {
      obj[head] = value;
    } else {
      if (!obj[head]) obj[head] = Array.isArray(value) ? [] : {};
      updateNestedProperty(obj[head], rest, value);
    }
  };

  const updatedObj = Array.isArray(obj) ? [...obj] : { ...obj };

  updateNestedProperty(updatedObj, path, value);

  return updatedObj;
};

export function getNestedValue(obj: unknown, path: string): any {
  if (obj === null || obj === undefined || typeof obj !== "object") {
    return undefined;
  }

  try {
    return path.split(".").reduce((acc: unknown, key: string) => {
      if (acc === null || acc === undefined || typeof acc !== "object") {
        return undefined;
      }

      return (acc as Record<string, unknown>)?.[key];
    }, obj);
  } catch {
    return undefined;
  }
}

/**
 * Sets a value at a deeply nested path, ensuring parent objects exist.
 */
export function setPath(obj: Record<string, any>, path: string, value: unknown): void {
  const parts = path.split(".");
  const leaf = parts.pop();

  if (leaf == null) return;

  const branch = parts.reduce((acc, key, i) => {
    if (!(key in acc) || typeof acc[key] !== "object" || acc[key] == null) {
      const nextKey = parts[i + 1] ?? leaf;

      // Check if nextKey is a valid non-negative integer string
      const isIndex = nextKey !== "" && !Number.isNaN(Number(nextKey));

      acc[key] = isIndex ? [] : {};
    }

    return acc[key];
  }, obj);

  branch[leaf] = value;
}

export function overlayDefined<T extends object>(base: T, overlay: Partial<T>): T {
  const result: T = { ...base };

  for (const key of Object.keys(overlay) as (keyof T)[]) {
    const value = overlay[key];

    if (value !== undefined) {
      result[key] = value;
    }
  }

  return result;
}

export const hasValue = (value: any): boolean => {
  if (value == null) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "object" && value.constructor === Object) {
    return Object.keys(value).length > 0;
  }

  // For numbers, booleans, etc., which are considered to have a value.
  return true;
};

/** Like hasValue, but checks nested leaves instead of counting object keys or array entries. */
export function hasDeepValue(value: unknown): boolean {
  if (value !== null && typeof value === "object") return Object.values(value).some(hasDeepValue);

  return hasValue(value);
}

// Helper function to check if a field is required
export function isFieldRequired(fields: RequiredFieldsForClassification, obj: any, fieldPath: string): boolean {
  if (!fields) return false;

  const requiredFields = fields.requiredFields;
  const conditionallyRequiredFields = fields.conditionallyRequiredFields;
  const alternativeRequiredFields = fields.alternativeRequiredFields || []; // Handle optional property

  // Check regular required fields
  if (requiredFields.includes(fieldPath)) return true;

  // Check conditional requirements dynamically
  for (const key in conditionallyRequiredFields) {
    const rule = conditionallyRequiredFields[key];

    if (rule.fields.includes(fieldPath) && shouldApplyConditionalRule(rule, obj)) {
      return true;
    }
  }

  // Check alternative required fields
  for (const group of alternativeRequiredFields) {
    // Check if the current fieldPath is part of this group
    if (group.includes(fieldPath)) {
      // Check if *any* field in the group has a value
      const anyFieldHasValue = group.some((altFieldPath) => {
        const value = getNestedValue(obj, altFieldPath);

        return value !== null && value !== undefined && value !== "" && !(Array.isArray(value) && value.length === 0);
      });

      // If no field in the group has a value, then this field (and all others in the group) is required
      if (!anyFieldHasValue) {
        return true;
      }

      // If at least one field has a value, then this specific field is not required *by this rule*.
      // We can break early since a field can only belong to one logic check here.
      break;
    }
  }

  return false;
}

export function isFieldEnabled(enabledFields: EnabledFields, fieldKey: string): boolean {
  if (!enabledFields) return true; // If no enabledFields specified, enable all

  if (enabledFields.includes(fieldKey)) return true;

  const parentField = fieldKey.split(".")[0];

  if (enabledFields.includes(parentField)) return true;

  return false;
}

const normalizeForComparison = (value: unknown): string =>
  Array.isArray(value)
    ? [...value]
        .map((entry) => String(entry).toLowerCase())
        .sort()
        .join(",")
    : String(value).toLowerCase();

export const analyzeObjectDifferences = (
  obj1: any,
  obj2: any | null,
  fields: string[],
  parentPath: string = ""
): { conflictingFields: string[]; newFields: string[] } => {
  const conflictingFields: string[] = [];
  const newFields: string[] = [];

  if (!obj1) {
    return {
      conflictingFields: [],
      newFields: [],
    };
  }

  if (!obj2) {
    // If obj2 is null, all fields in obj1 are considered new
    return {
      conflictingFields: [],
      newFields: fields.filter((field) => getNestedValue(obj1, field)),
    };
  }

  for (const key of Object.keys(obj1)) {
    const val1 = getNestedValue(obj1, key);
    const val2 = getNestedValue(obj2, key);

    const currentPath = parentPath ? `${parentPath}.${key}` : key;

    if (val1 && typeof val1 === "object" && !Array.isArray(val1) && val2) {
      const nestedDifferences = analyzeObjectDifferences(val1, val2, fields, currentPath);

      conflictingFields.push(...nestedDifferences.conflictingFields);

      newFields.push(...nestedDifferences.newFields);
    } else {
      if (!fields.includes(currentPath)) {
        continue;
      }

      if (hasValue(val1) && !hasValue(val2)) {
        newFields.push(currentPath);
      }

      if (hasValue(val1) && hasValue(val2) && normalizeForComparison(val1) !== normalizeForComparison(val2)) {
        conflictingFields.push(currentPath);
      }
    }
  }

  return { conflictingFields, newFields };
};

export function cleanJsonString(jsonString: string): string {
  if (!jsonString || typeof jsonString !== "string") {
    return "{}";
  }

  // Remove all markdown code blocks (```json, ```, etc.)
  let cleanedResponse = jsonString.replace(/```[a-z]*\s*/gi, "").replace(/```\s*$/g, "");

  // Remove any remaining backticks
  cleanedResponse = cleanedResponse.replace(/`/g, "");

  // Remove common AI response prefixes and explanations
  cleanedResponse = cleanedResponse.replace(
    /^(as requested|here is|here's|the json|json response|here you go|i've provided|below is|attached is):?\s*/i,
    ""
  );

  // Remove markdown formatting (**text**, *text*, etc.)
  cleanedResponse = cleanedResponse
    .replace(/\*\*[^*]*\*\*/g, "") // **bold**
    .replace(/\*[^*]*\*/g, "") // *italic*
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1"); // [text](url) -> text

  // Remove HTML tags
  cleanedResponse = cleanedResponse.replace(/<[^>]*>/g, "");

  // Remove extra whitespace and normalize
  cleanedResponse = cleanedResponse.replace(/\s+/g, " ").trim();

  // Try to find valid JSON using multiple strategies
  const strategies = [
    // Strategy 1: Brace counting for objects
    () => {
      let braceCount = 0;
      let startIndex = -1;
      let endIndex = -1;

      for (let i = 0; i < cleanedResponse.length; i++) {
        if (cleanedResponse[i] === "{") {
          if (braceCount === 0) {
            startIndex = i;
          }

          braceCount++;
        } else if (cleanedResponse[i] === "}") {
          braceCount--;

          if (braceCount === 0 && startIndex !== -1) {
            endIndex = i;
            break;
          }
        }
      }

      if (startIndex !== -1 && endIndex !== -1) {
        return cleanedResponse.substring(startIndex, endIndex + 1);
      }

      return null;
    },

    // Strategy 2: Find the largest valid JSON object
    () => {
      const jsonMatches = cleanedResponse.match(/\{[\s\S]*\}/g);

      if (jsonMatches && jsonMatches.length > 0) {
        // Find the largest (most complete) JSON object
        return jsonMatches.reduce((largest, current) => (current.length > largest.length ? current : largest));
      }

      return null;
    },

    // Strategy 3: Look for array structures
    () => {
      const arrayMatches = cleanedResponse.match(/\[[\s\S]*\]/g);

      if (arrayMatches && arrayMatches.length > 0) {
        return arrayMatches.reduce((largest, current) => (current.length > largest.length ? current : largest));
      }

      return null;
    },

    // Strategy 4: Try to fix common JSON issues
    () => {
      // Remove trailing commas before closing braces/brackets
      let fixed = cleanedResponse.replace(/,(\s*[}\]])/g, "$1").replace(/,(\s*})/g, "$1");

      // Try to find a complete object/array
      const objectMatch = fixed.match(/\{[\s\S]*\}/);

      if (objectMatch) return objectMatch[0];

      const arrayMatch = fixed.match(/\[[\s\S]*\]/);

      if (arrayMatch) return arrayMatch[0];

      return null;
    },
  ];

  // Try each strategy until one works
  for (const strategy of strategies) {
    try {
      const result = strategy();

      if (result) {
        // Validate that it's actually valid JSON
        JSON.parse(result);

        return result;
      }
    } catch (e) {
      // Continue to next strategy
      continue;
    }
  }

  // If all strategies fail, return empty object
  return "{}";
}

/**
 * Converts all empty string values in an object to null.
 * @param obj - The object to convert.
 * @param includeNested - Whether to recursively convert nested objects.
 * @returns A new object with all empty string values converted to null.
 */
export function convertEmptyStringValuesToNull<T extends Record<string, unknown>>(
  obj: T,
  includeNested: boolean = false
): T {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value === "") {
      result[key] = null;
    } else if (Array.isArray(value)) {
      result[key] = includeNested ? value.map((v) => (v === "" ? null : v)) : value;
    } else if (includeNested && value && typeof value === "object") {
      result[key] = convertEmptyStringValuesToNull(value as Record<string, unknown>, true);
    } else {
      result[key] = value;
    }
  }

  return result as T;
}

/**
 * Converts a TypeScript enum to an array of { value, label } option objects.
 * Useful for populating select dropdowns from enums.
 * Default label capitalizes the first character when no labelMap is provided.
 *
 * @param enumObj - The enum to convert
 * @param labelMap - Optional function to customize the label (defaults to value with first char capitalized)
 */
export function enumToOptions<T extends string | number>(
  enumObj: Record<string, T>,
  labelMap?: (value: T) => string
): { value: T; label: string }[] {
  return Object.values(enumObj).map((value) => ({
    value,
    label: labelMap ? labelMap(value) : capitalizeFirst(String(value)),
  }));
}

/**
 * Used to create a type for object errors, where each key is field in the object and each value is an error string or undefined.
 * Supports nested fields.
 * @param T - The object type to create object errors for
 */
export type ObjectErrors<T> = {
  [K in keyof T]?: T[K] extends object | null | undefined
    ? T[K] extends Array<any>
      ? string
      : T[K] extends null | undefined
        ? string
        : ObjectErrors<T[K]>
    : string;
};

/**
 * Helper to set a nested error message in an object given a path array.
 */
function setNestedError<T>(errors: ObjectErrors<T>, path: PropertyKey[], message: string): void {
  let current: any = errors;

  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];

    if (!(key in current) || typeof current[key] !== "object") {
      current[key] = {};
    }

    current = current[key];
  }

  current[path[path.length - 1]] = message;
}

/**
 * Validates object data against a Zod schema and returns validity and errors.
 * @param schema - The Zod schema to validate against
 * @param objectData - The object data to validate
 * @param fieldsToValidate - Optional array of field paths to validate. Supports arbitrarily nested paths like "address.city"
 */
export function validateObject<T>(
  schema: ZodSchema,
  objectData: T,
  fieldsToValidate?: string[]
): { isValid: boolean; errors: ObjectErrors<T> } {
  const result = schema.safeParse(objectData);

  if (result.success) {
    return { isValid: true, errors: {} };
  }

  const errors: ObjectErrors<T> = {};

  for (const issue of result.error.issues) {
    const fieldPath = issue.path.join(".");

    const shouldIncludeError =
      !fieldsToValidate || fieldsToValidate.some((field) => fieldPath === field || fieldPath.startsWith(field + "."));

    if (shouldIncludeError) {
      setNestedError(errors, issue.path, formatZodIssueMessage(issue, objectData));
    }
  }

  const isValid = Object.keys(errors).length === 0;

  return { isValid, errors };
}

/**
 * Converts string "null" values to actual null values in parsed JSON objects.
 * This is useful for cleaning up AI model responses that return "null" as strings.
 * @param obj - The object to convert.
 * @returns A new object with string "null" values converted to actual null.
 */
export function convertStringNullsToNull<T>(obj: T): T {
  if (obj === "null") {
    return null as T;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertStringNullsToNull) as T;
  }

  if (obj && typeof obj === "object") {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      result[key] = convertStringNullsToNull(value);
    }

    return result as T;
  }

  return obj;
}

// Helper function to check if conditional rule should be applied
function shouldApplyConditionalRule(rule: any, dataObject: any): boolean {
  // First, check if the dependsOn field has a value
  const parentFieldValue = getNestedValue(dataObject, rule.dependsOn);

  if (!hasValue(parentFieldValue)) {
    return false;
  }

  // If no condition, apply the rule
  if (!rule.condition) {
    return true;
  }

  // Check the condition
  const conditionFieldValue = getNestedValue(dataObject, rule.condition.field);
  const conditionValue = rule.condition.value;

  // Normalize undefined to null for comparison
  const normalizedFieldValue = conditionFieldValue === undefined ? null : conditionFieldValue;

  return Array.isArray(conditionValue)
    ? conditionValue.includes(normalizedFieldValue)
    : normalizedFieldValue === conditionValue;
}

/**
 * Formats a field path to a human-readable name.
 * Handles both block-specific paths (e.g., "patient.givenName") and generic fields
 * that appear in multiple blocks (e.g., "referringProvider.npiNumber" -> checks "npiNumber").
 *
 * Strategy:
 * 1. Check exact field path match (e.g., "patient.givenName")
 * 2. If not found and path contains a dot, strip the first segment (block prefix) and check the rest
 * 3. If still not found, format using sentenceCase fallback
 *
 * Examples:
 * - "patient.givenName" -> "Patient First Name" (from mapping)
 * - "referringProvider.npiNumber" -> "NPI number" (strips "referringProvider", checks "npiNumber")
 * - "patient.homeAddress.line1" -> "Patient Home Address Line 1" (strips "patient", checks "homeAddress.line1")
 * - "unknownField" -> "Unknown field" (formatted fallback)
 */
function formatFieldPath(fieldPath: string, fieldToFriendlyNameMap: Record<string, string>): string {
  // 1. Check exact match first
  if (fieldToFriendlyNameMap?.[fieldPath]) {
    return fieldToFriendlyNameMap[fieldPath];
  }

  // 2. If path contains dots, strip the first segment (block type prefix) and check the rest
  // e.g., "referringProvider.npiNumber" -> check "npiNumber"
  // e.g., "patient.homeAddress.line1" -> check "homeAddress.line1"
  if (fieldPath.includes(".")) {
    const segments = fieldPath.split(".");
    const pathWithoutBlockPrefix = segments.slice(1).join(".");

    if (fieldToFriendlyNameMap?.[pathWithoutBlockPrefix]) {
      return fieldToFriendlyNameMap[pathWithoutBlockPrefix];
    }
  }

  // 3. Fallback: format using sentenceCase
  const segments = fieldPath.split(".");
  const formattedSegments = segments.map((segment) => sentenceCase(segment));

  return formattedSegments.join(" ");
}

// Helper function to collect missing fields from a list
function collectMissingFields(
  fields: string[],
  dataObject: any,
  fieldToFriendlyNameMap: Record<string, string>
): string[] {
  const missing: string[] = [];

  if (!fields) {
    return missing;
  }

  fields.forEach((field) => {
    if (!hasValue(getNestedValue(dataObject, field))) {
      const friendlyName = formatFieldPath(field, fieldToFriendlyNameMap);

      missing.push(friendlyName);
    }
  });

  return missing;
}

// Generic utility function to validate fields and return missing fields
function validateFieldsWithMissingFields(
  fieldsConfig: RequiredFieldsForClassification | RequestedFieldsForClassification,
  dataObject: any,
  returnPropertyName: string = "missingFields"
): any {
  const fieldToFriendlyNameMap: Record<string, string> = FIELD_TO_FRIENDLY_NAME;

  if (!fieldsConfig) {
    return { isValid: true, [returnPropertyName]: [] };
  }

  const missingFields: string[] = [];

  // Check regular fields
  const regularFields = (fieldsConfig as any).requiredFields || (fieldsConfig as any).requestedFields;

  missingFields.push(...collectMissingFields(regularFields, dataObject, fieldToFriendlyNameMap));

  // Check alternative fields
  const alternativeFields =
    (fieldsConfig as any).alternativeRequiredFields || (fieldsConfig as any).alternativeRequestedFields;

  (alternativeFields || []).forEach((group: string[]) => {
    const hasAnyField = group.some((field) => hasValue(getNestedValue(dataObject, field)));

    if (!hasAnyField) {
      const friendlyNames = group.map((field) => formatFieldPath(field, fieldToFriendlyNameMap));

      missingFields.push(`One of: ${friendlyNames.join(", ")}`);
    }
  });

  // Check conditional fields
  const conditionalFields =
    (fieldsConfig as any).conditionallyRequiredFields || (fieldsConfig as any).conditionallyRequestedFields;

  if (conditionalFields) {
    Object.values(conditionalFields).forEach((rule: any) => {
      if (shouldApplyConditionalRule(rule, dataObject)) {
        missingFields.push(...collectMissingFields(rule.fields, dataObject, fieldToFriendlyNameMap));
      }
    });
  }

  // Deduplicate fields
  const uniqueMissingFields = [...new Set(missingFields)];

  return { isValid: uniqueMissingFields.length === 0, [returnPropertyName]: uniqueMissingFields };
}

export function validateRequiredFieldsWithMissingFields(
  requiredFields: RequiredFieldsForClassification,
  dataObject: any
): { isValid: boolean; missingFields: string[] } {
  return validateFieldsWithMissingFields(requiredFields, dataObject, "missingFields");
}

export function validateRequestedFieldsWithMissingFields(
  requestedFields: RequestedFieldsForClassification,
  dataObject: any
): { isValid: boolean; missingRequestedFields: string[] } {
  return validateFieldsWithMissingFields(requestedFields, dataObject, "missingRequestedFields");
}

export const isEql = (a: any, b: any): boolean => {
  if (a === b) return true;

  if (a == null || b == null) return a === b;

  if (typeof a === "object" && typeof b === "object") {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  return a === b;
};

export const isEqlArr = (a: any[], b: any[]): boolean => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;

  if (a.length !== b.length) return false;

  return a.every((item, index) => {
    const otherItem = b[index];

    return isEql(item, otherItem);
  });
};
